/**
 * "/admin" — the debug reports users sent from the on-screen debug log
 * (features/debug/DebugLogPanel.tsx → POST /api/debug-reports). Newest first;
 * each card shows the reporter, their note, where they were and the last
 * lines of their log, with the rest on demand. "Dismiss" deletes the row —
 * the `[debug-report]` line in the server log stays.
 */
import type { DebugReportItem } from "@kola/shared";
import { useEffect, useState } from "react";

import { deleteDebugReport, fetchDebugReports } from "../../core/api.js";
import { formatDebugLog, type DebugLogEntry } from "../../core/debug-log.js";
import { showToast } from "../../ui/toast.js";
import { DebugEntryLine } from "../debug/DebugEntryLine.js";

/** Lines shown before "Show all" — the tail is where the failure usually is. */
const PREVIEW_LINES = 8;

export function DebugReportsSection() {
  const [items, setItems] = useState<DebugReportItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      setItems((await fetchDebugReports()).items);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const dismiss = async (id: string) => {
    try {
      await deleteDebugReport(id);
      setItems((current) => current?.filter((item) => item.id !== id) ?? null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to dismiss the report");
    }
  };

  return (
    <section className="admin-section">
      <div className="admin-section-head">
        <h2>Debug reports</h2>
        <span className="admin-card-hint">
          Reports sent from generation errors or with "Send to owner" in the debug panel.
          Each report is also a "[debug-report]" line in the server log.
        </span>
      </div>

      {error !== null && <div className="error-note">{error}</div>}
      {items === null && error === null && <div className="empty-state">Loading…</div>}
      {items !== null && items.length === 0 && (
        <div className="empty-state">No reports. When someone sends one it appears here.</div>
      )}
      {items !== null && items.length > 0 && (
        <div className="admin-card-list">
          {items.map((report) => (
            <ReportCard key={report.id} report={report} onDismiss={() => void dismiss(report.id)} />
          ))}
        </div>
      )}
    </section>
  );
}

/** The report's entries in the panel's own shape, for the shared text renderer. */
function asLogEntries(report: DebugReportItem): DebugLogEntry[] {
  return report.entries.map((entry, index) => ({ ...entry, id: index, count: 1 }));
}

function ReportCard({ report, onDismiss }: { report: DebugReportItem; onDismiss: () => void }) {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? report.entries : report.entries.slice(-PREVIEW_LINES);
  const when = new Date(report.createdAt).toLocaleString();
  const facts = [
    when,
    `v${report.context.version}`,
    `${report.errorCount} ${report.errorCount === 1 ? "error" : "errors"} in ${report.entries.length} ${report.entries.length === 1 ? "line" : "lines"}`,
    ...(report.context.boardId ? [`board ${report.context.boardId}`] : []),
    ...(report.context.connection ? [`socket ${report.context.connection}`] : []),
  ];

  const copy = async () => {
    const text =
      `From ${report.reporterName} <${report.reporterEmail}> at ${report.createdAt}\n` +
      (report.note ? `Note: ${report.note}\n\n` : "\n") +
      formatDebugLog(asLogEntries(report), report.context);
    try {
      await navigator.clipboard.writeText(text);
      showToast("Report copied");
    } catch {
      showToast("Couldn't copy — your browser blocked clipboard access");
    }
  };

  return (
    <div className="admin-card debug-report-card">
      <div className="admin-card-header">
        <h2>
          {report.reporterName}
          <span className="debug-report-email">{report.reporterEmail}</span>
        </h2>
        <span className="admin-card-hint">{facts.join(" · ")}</span>
      </div>
      {report.note && <p className="debug-report-note">{report.note}</p>}
      <div className="debug-report-meta">
        <span>{report.context.url}</span>
        <span>{report.context.userAgent}</span>
      </div>
      {report.entries.length === 0 ? (
        <div className="empty-state">The log was empty when it was sent.</div>
      ) : (
        <ol className="debug-log-list debug-report-entries">
          {[...shown].reverse().map((entry, index) => (
            <DebugEntryLine key={`${entry.ts}-${index}`} entry={entry} />
          ))}
        </ol>
      )}
      <div className="admin-card-actions">
        {report.entries.length > PREVIEW_LINES && (
          <button type="button" className="btn btn-ghost" onClick={() => setShowAll((v) => !v)}>
            {showAll ? `Show last ${PREVIEW_LINES}` : `Show all ${report.entries.length} lines`}
          </button>
        )}
        <button type="button" className="btn btn-ghost" onClick={() => void copy()}>
          Copy
        </button>
        <button type="button" className="btn btn-danger" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    </div>
  );
}
